import { PractiseUserRelation } from 'data/orgnisation-management/types';
import { handleResponse, axiosInstance, API, graphqlAxiosInstance } from './helper';
import { getGroupbyId, getRealmUserInfo } from './keycloak';

const getOrganisationDetails = (organisationId: string) =>
  handleResponse(
    axiosInstance.get(API.getOrganisationDetails, {
      params: { organisationId }
    })
  );

const getAllOrganisationDetails = (organisationId: string, role: string) =>
  handleResponse(
    axiosInstance.get(API.getAllOrganisationDetails, {
      params: { organisationId, role }
    })
  );

const getOrganisationDashboardActivity = (payload: {
  startTimestamp: string;
  endTimestamp: string;
}) => handleResponse(axiosInstance.post(API.getOrganisationDashboardActivity, payload));

const getOrganisationDashboardLineActivity = (payload: { rangeType: string }) =>
  handleResponse(
    axiosInstance.get(API.getOrganisationDashboardLineActivity, {
      params: payload
    })
  );

const getOrganisationStats = () => handleResponse(axiosInstance.get(API.getOrganisationStats));

type TimeRangeData = {
  startTimeStamp: string;
  endTimeStamp: string;
  organisationId: string; // UUID
};

const getOrganisationActivityDetails = (payload: TimeRangeData) =>
  handleResponse(axiosInstance.post(API.getOrganisationActivityDetails, payload));

const fetchOrganisationManagementTableData = async (limit = 10, created_at?: string) => {
  const query = `query get_organisation_management_table_data_v1($limit: Int!, $created_at: timestamptz) {
  practiseOrganisations(order_by: {created_at: desc}, limit: $limit, where: {created_at: {_lt: $created_at}}) {
    id
    created_at
    contact
    logoUrl
    registrationNumber
    organisationPractiseProfileRelation(limit: 6) {
      practiseUserLivePicRelation {
        imageContent
        practiseAdminId
      }
    }
  }
  totalCount: practiseOrganisations_aggregate {
    aggregate {
      count(columns: id)
    }
  }
}`;
  const variables = { limit, created_at: created_at || new Date().toISOString() };
  const response = await graphqlAxiosInstance.post(`/${API.hasuraGraphql}`, {
    query,
    variables
  });
  return response;
};

interface PractiseUserLivePicRelation {
  imageContent: string;
  practiseAdminId: string;
}

interface OrganisationPractiseProfileRelation {
  practiseUserLivePicRelation: PractiseUserLivePicRelation;
}

export interface PractiseOrganisation {
  id: string;
  created_at: string;
  contact: string | null;
  logoUrl: string | null;
  registrationNumber: string | null;
  organisationPractiseProfileRelation: OrganisationPractiseProfileRelation[];
}

interface TotalCount {
  aggregate: {
    count: number;
  };
}

interface FetchResponse {
  data: {
    data: {
      practiseOrganisations: PractiseOrganisation[];
      totalCount: TotalCount;
    };
  };
}

const getOrganisationManagementList = async (
  limit = 10,
  created_at?: string,
  generateSignedUrl?: (url: string, token: string) => Promise<string>
) => {
  const response: FetchResponse = await fetchOrganisationManagementTableData(limit, created_at);
  const basicDetailsList = response?.data?.data?.practiseOrganisations || [];
  const userAccessToken = localStorage.getItem('user_access_token') || '';

  const enrichedList = await Promise.all(
    basicDetailsList.map(async (org, index) => {
      const {
        id,
        logoUrl,
        contact,
        created_at: createdAt,
        registrationNumber,
        organisationPractiseProfileRelation = []
      } = org;

      // wrap org fetch safely
      let orgData: { name?: string } | null = null;
      try {
        orgData = await getGroupbyId(id, userAccessToken);
      } catch (err) {
        console.warn(`getGroupbyId failed for org ${id}:`, err);
      }

      const orgImage =
        logoUrl && generateSignedUrl
          ? await generateSignedUrl(logoUrl, userAccessToken)
          : undefined;

      const connectedUsers = await Promise.all(
        organisationPractiseProfileRelation
          .filter((relation: PractiseUserRelation) => {
            const imageContent = relation?.practiseUserLivePicRelation?.imageContent;
            const adminId = relation?.practiseUserLivePicRelation?.practiseAdminId;
            return Boolean(imageContent && adminId);
          })

          .map(async (relation: PractiseUserRelation) => {
            const { imageContent, practiseAdminId } = relation.practiseUserLivePicRelation;

            let firstName = '';
            let lastName = '';
            try {
              const userInfo = await getRealmUserInfo(practiseAdminId, userAccessToken);
              firstName = userInfo?.firstName || '';
              lastName = userInfo?.lastName || '';
            } catch (err) {
              console.warn(`getRealmUserInfo failed for user ${practiseAdminId}:`, err);
            }

            const signedUrl =
              generateSignedUrl && (await generateSignedUrl(imageContent, userAccessToken));

            return {
              name: [firstName, lastName].filter(Boolean).join(' ') || '',
              image: signedUrl
            };
          })
      );

      return {
        organisationName: orgData?.name || '',
        registrationNumber: registrationNumber || '',
        no: index,
        organisationId: id,
        verificationDate: createdAt,
        lastUpdatedAt: createdAt,
        phoneNumber: contact || '',
        image: orgImage || '',
        connectedUsers
      };
    })
  );

  return {
    data: enrichedList,
    hasMore: enrichedList.length === limit,
    totalCount: response?.data?.data?.totalCount?.aggregate?.count || 0,
    lastUpdatedAt:
      basicDetailsList.length > 0 ? basicDetailsList[basicDetailsList.length - 1].created_at : null
  };
};

export {
  getOrganisationDetails,
  getAllOrganisationDetails,
  getOrganisationDashboardActivity,
  getOrganisationDashboardLineActivity,
  getOrganisationStats,
  getOrganisationActivityDetails,
  getOrganisationManagementList
};
